import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@core-ui/Components/ui/button';
import { Badge } from '@core-ui/Components/ui/badge';
import { cn } from '@core-ui-lib/utils';

import { Play, Pause, FileText, Users, Mic, Download, Loader2, Pencil, Settings, Shuffle, PhoneOff, PhoneCall, Phone, ExternalLink } from 'lucide-react';

import useContactsApi, { type ContactInfoDto } from '@features/contacts/hooks/useContactsApi';

import useWizardStore from '../../store/useWizardStore';
import type { WizardStep } from '../../types/wizard.types';
import { OngoingCallAction } from '../../types/wizard.types';
import ContactsPreviewModal from '../ContactsPreviewModal';
import IntentPreviewModal from '../IntentPreviewModal';
import { HubspotLogoTyped, OutreachLogoTyped, SalesforceLogoTyped } from '@assets/index.ts';
import { type ApiResponse } from '@types';
import { useOutreachViewModel } from '@features/contacts/hooks/useOutreachViewModel.ts';
import { useSalesforceViewModel } from '@features/contacts/hooks/useSalesforceViewModel.ts';
import { useHubspotViewModel } from '@features/contacts/hooks/useHubspotViewModel.ts';
import { microServicesRoutes } from '@constants';
import { edgeApi } from '@lib/apiClients.ts';
import { logWarning } from '@lib/logger/logWarning.ts';

interface SummaryStepProps {
  onEditStep: (step: WizardStep) => void;
  activeTab?: string;
  salesforceNonce?: string;
  outreachContacts?: {
    nonce? : string;
    stepId?: number;
    sequenceId?: number
  };
}

export default function SummaryStep ({
  onEditStep,
}: SummaryStepProps): React.JSX.Element {
  const { t } = useTranslation();
  const { selectedIntent, selectedContactList, selectedVoicemail,
    shuffleContacts, ongoingCallAction, currentContactsTab,
    salesforceNonce, outreachData, contactsCount, setContactsCount, salesforceURL, hubspotPortalId
  } = useWizardStore();
  const { getContactsByContactListIdAsync, exportContactsOfListToCsv } = useContactsApi();
  const { retrieveOutreachContacts } = useOutreachViewModel();
  const { retrieveSalesforceContacts } = useSalesforceViewModel();
  const { retrieveHubspotContacts } = useHubspotViewModel();

  const [showContactsModal, setShowContactsModal] = useState(false);
  const [showIntentModal, setShowIntentModal] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackProgress, setPlaybackProgress] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [counterLoading, setCounterLoading] = useState(false);
  const [callingFromNumber, setCallingFromNumber] = useState<string>('');
  const hasSalesforce = salesforceURL !== '' && currentContactsTab === 'salesforce';
  const hasHubspot = hubspotPortalId !== '' && currentContactsTab === 'hubspot';

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  
  // Load callerID number
  const fetchCallingNumber = useCallback(async (): Promise<void> => {
    try {
      const url = microServicesRoutes.edge.get.twilio.currentNumber;
      const response = await edgeApi.get<string>(url);
      if (response.data !== undefined && response.data !== '') {
        setCallingFromNumber(response.data);
      }
    } catch (error) {
      // Not critical, just log
      void logWarning('Failed to fetch calling number', 'SessionDashboard/fetchCallingNumber', { error });
    }
  }, []);

  useEffect(() => {
    void fetchCallingNumber();
  }, []);
  
  // Load audio duration on mount
  useEffect(() => {
    if (selectedVoicemail === null) {
      return;
    }
    const audioUrl = selectedVoicemail.recordingUrl ?? selectedVoicemail.url;
    if (audioUrl === undefined) {
      return;
    }
    const audio = new Audio(audioUrl);
    audio.onloadedmetadata = () => {
      const duration = audio.duration;
      if (Number.isFinite(duration) && duration > 0) {
        setAudioDuration(Math.floor(duration));
      }
    };
    // Cleanup
    return () => {
      audio.src = '';
    };
  }, [selectedVoicemail]);

  const externalLink = useMemo(() => {
    const listId = selectedContactList?.id;

    if (hasSalesforce) {
      const path = (listId != null) ? `r/Report/${listId}/view` : 'o/Contact/list';
      return {
        url: `${salesforceURL}/lightning/${path}`,
        windowName: 'salesforceWindow',
      };
    }

    if (hasHubspot) {
      const path = (listId != null) ? `objectLists/${listId}/filters` : 'objectLists';
      return {
        url: `https://app.hubspot.com/contacts/${hubspotPortalId}/${path}`,
        windowName: 'hubspotWindow',
      };
    }

    return null;
  }, [hasSalesforce, hasHubspot, salesforceURL, hubspotPortalId]);

  const handleOpenExternalContactList = useCallback(() => {
    if (externalLink === null) {
      return;
    }

    window.open( externalLink.url, externalLink.windowName, 'width=900,height=700,scrollbars=yes,resizable=yes');
  }, [externalLink]);

  useEffect(() => {
    return () => {
      if (audioRef.current !== null) {
        audioRef.current.pause();
      }
      if (progressIntervalRef.current !== null) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, []);

  const selectContactsFetchCallback: (activeTab: string) => Promise<ContactInfoDto[] | undefined> = async () => {
    setCounterLoading(true);

    if (currentContactsTab === 'outreach' && outreachData !== undefined) {
      const { nonce, outreachSequenceStep: stepId, outreachSequenceId: sequenceId } = outreachData;
      return await retrieveOutreachContacts(nonce ?? '',  Number(sequenceId) ?? 0,  Number(stepId) ?? 0, true);
    }

    if (currentContactsTab === 'hubspot' && selectedContactList?.id !== null) {
      return await retrieveHubspotContacts(selectedContactList?.id ?? '');
    }

    if (currentContactsTab === 'salesforce' && selectedContactList?.id !== null && salesforceNonce !== undefined) {
      return await retrieveSalesforceContacts(salesforceNonce, selectedContactList?.id ?? '');
    }

    const response: ApiResponse<ContactInfoDto[]> = await getContactsByContactListIdAsync(selectedContactList?.id ?? '');

    return response.data;
  };

  const truncateText = (text: string, maxLength: number): string => {
    if (text.length <= maxLength) {
      return text;
    }
    return `${text.substring(0, maxLength)}...`;
  };

  const getLanguageBadge = (text: string): string => {
    const spanishPattern = /hola|buenos|gracias|llamando|nombre|spanish|español|es|mx/i;
    return spanishPattern.test(text) ? 'ES' : 'EN';
  };

  const outreachContactsParams = {
    sequenceId: Number(outreachData?.outreachSequenceStep ?? 0),
    stepId:  Number(outreachData?.outreachSequenceId ?? 0),
    nonce: outreachData?.nonce ?? '',
  };

  const handleDownloadCsv = async (): Promise<void> => {
    if (selectedContactList === null) {
      return;
    }
    setIsDownloading(true);
    const result = await getContactsByContactListIdAsync(selectedContactList.id);
    if (result.success && result.data !== undefined) {
      exportContactsOfListToCsv({
        contacts: result.data,
        filename: selectedContactList.title,
      });
    }
    setIsDownloading(false);
  };

  const handlePlayPause = (): void => {
    if (selectedVoicemail === null) {
      return;
    }

    const audioUrl = selectedVoicemail.recordingUrl ?? selectedVoicemail.url;
    if (audioUrl === undefined) {
      return;
    }

    if (isPlaying) {
      if (audioRef.current !== null) {
        audioRef.current.pause();
      }
      if (progressIntervalRef.current !== null) {
        clearInterval(progressIntervalRef.current);
      }
      setIsPlaying(false);
    } else {
      if (audioRef.current !== null) {
        audioRef.current.pause();
      }
      if (progressIntervalRef.current !== null) {
        clearInterval(progressIntervalRef.current);
      }

      audioRef.current = new Audio(audioUrl);

      audioRef.current.onloadedmetadata = () => {
        if (audioRef.current !== null) {
          const duration = audioRef.current.duration;
          if (Number.isFinite(duration) && duration > 0) {
            setAudioDuration(Math.floor(duration));
          }
        }
      };

      audioRef.current.play().catch(() => {});
      setIsPlaying(true);

      progressIntervalRef.current = setInterval(() => {
        if (audioRef.current !== null) {
          const progress = (audioRef.current.currentTime / audioRef.current.duration) * 100;
          setPlaybackProgress(Number.isFinite(progress) ? progress : 0);
        }
      }, 100);

      audioRef.current.onended = () => {
        setIsPlaying(false);
        setPlaybackProgress(0);
        if (progressIntervalRef.current !== null) {
          clearInterval(progressIntervalRef.current);
        }
      };
    }
  };

  const formatDuration = (seconds: number): string => {
    if (seconds <= 0) {
      return '—';
    }
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getVoicemailDuration = (): number => {
    if (audioDuration > 0) {
      return audioDuration;
    }
    if (selectedVoicemail?.durationSec !== undefined && selectedVoicemail.durationSec > 0) {
      return selectedVoicemail.durationSec;
    }
    // Check all possible duration properties
    const raw = selectedVoicemail?.duration
      ?? selectedVoicemail?.recordingDuration
      ?? selectedVoicemail?.duration_seconds
      ?? selectedVoicemail?.length
      ?? 0;
    const parsed = typeof raw === 'string' ? parseInt(raw, 10) : raw;
    return Number.isFinite(parsed) ? parsed : 0;
  };

  // Generate organic waveform bars
  const generateWaveformBars = (): number[] => {
    const vmId = selectedVoicemail?.id ?? selectedVoicemail?._id ?? 'default';
    const seed = vmId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const bars: number[] = [];
    for (let i = 0; i < 16; i++) {
      const height = 30 + ((seed * (i + 1) * 7) % 70);
      bars.push(height);
    }
    return bars;
  };

  function renderIntegrationIcon (integration: string): string {
    let icon = '';
    switch (integration) {
    case 'outreach': icon = OutreachLogoTyped; break;
    case 'hubspot': icon = HubspotLogoTyped; break;
    case 'salesforce': icon =  SalesforceLogoTyped; break;
    }
    return icon;
  }

  function renderIntegrationLabel (integration: string): string {
    let label = '';
    switch (integration) {
    case 'outreach': label = 'Outreach'; break;
    case 'hubspot': label = 'Hubspot'; break;
    case 'salesforce': label =  'Salesforce'; break;
    }
    return label;
  }
  
  const calculateContactsCount: () => Promise<void> = async ()=> {
    if (selectedContactList?.id != null) {
      const result = await selectContactsFetchCallback(currentContactsTab);

      const resultLength = result?.length ?? 0;
      setContactsCount(resultLength);
      setCounterLoading(false);

      if (currentContactsTab === 'outreach') {
        console.log('outreach', result?.length);
      }
    }
  };

  useMemo(()=>{
    void calculateContactsCount();
  }, [selectedContactList]);

  return (
    <div>
      <div className="flex justify-between w-full">
        <div className="mb-4">
          <h3 className="text-lg font-semibold">{t('wizard.summary.title')}</h3>
          <p className="text-sm text-muted-foreground">{t('wizard.summary.description')}</p>
        </div>
        <div className="flex mb-4 mt-4 justify-end items-center">
          <Phone className="h-4 w-4 text-[var(--phone-check)]" />
          <h3 className="text-md ml-2">{`${t('wizard.summary.callerId')}`}</h3>
          <h3 className="text-md font-semibold ml-2">{callingFromNumber}</h3>
        </div>
      </div>

      <div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* Intent Card */}
          <div className="rounded-lg border border-border p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <FileText className="h-4 w-4" />
                {t('wizard.summary.intent')}
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 gap-1.5 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                onClick={() => {
                  onEditStep(1);
                }}
              >
                <Pencil className="h-3.5 w-3.5" />
                {t('wizard.summary.select')}
              </Button>
            </div>
            {selectedIntent !== null ? (
              <div className="mt-3">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{selectedIntent.title}</span>
                  <Badge variant="secondary" className="rounded-full text-xs">
                    {getLanguageBadge(selectedIntent.body)}
                  </Badge>
                </div>
                <p className="mt-1 text-sm text-muted-foreground break-all">
                  {truncateText(selectedIntent.body, 100)}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setShowIntentModal(true);
                  }}
                  className="mt-1 text-sm text-primary hover:underline"
                >
                  {t('wizard.summary.viewMore')}
                </button>
              </div>
            ) : (
              <p className="mt-3 text-sm text-muted-foreground italic">
                {t('wizard.summary.notSelected')}
              </p>
            )}
          </div>

          {/* Contacts Card */}
          <div className="rounded-lg border border-border p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <Users className="h-4 w-4" />
                {t('wizard.summary.contacts')}
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 gap-1.5 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                onClick={() => {
                  onEditStep(2);
                }}
              >
                <Pencil className="h-3.5 w-3.5" />
                {t('wizard.summary.select')}
              </Button>
            </div>
            {selectedContactList !== null ? (
              <div className="mt-3">
                <span className="font-medium">
                  <a
                    href={externalLink?.url}
                    className="text-primary hover:underline"
                    onClick={(e) => {
                      e.preventDefault();
                      handleOpenExternalContactList();
                    }}>
                    {selectedContactList.title}
                    <ExternalLink className="w-4 h-4 ml-1 inline-block" />
                  </a>
                </span>
                {(currentContactsTab === 'outreach' || currentContactsTab === 'hubspot' || currentContactsTab === 'salesforce') &&
                <div className="mt-1 flex items-center gap-2 text-sm">
                  <img src={renderIntegrationIcon(currentContactsTab)} alt={'icon'} className="w-8 h-8 mt-2 mb-2"/>
                  {renderIntegrationLabel(currentContactsTab)}
                </div>}
                <div className="mt-1 flex items-center gap-4 text-sm">
                  <button
                    type="button"
                    onClick={() => {
                      setShowContactsModal(true);
                    }}
                    className="text-primary hover:underline"
                  >
                    {t('wizard.summary.viewContacts')}
                  </button>
                  {selectedContactList.type === 'CSV' &&
                    <button
                      type="button"
                      onClick={() => {
                        void handleDownloadCsv();
                      }}
                      disabled={isDownloading}
                      className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                    >
                      {isDownloading ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Download className="h-3 w-3" />
                      )}
                      {t('wizard.summary.downloadCsv')}
                    </button>}

                  {counterLoading ? (
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  ) : (<p className={`${contactsCount === 0 ? 'text-destructive' : ''} font-bold`}>
                    {contactsCount !== 0 && <span>{contactsCount} </span>}
                    {`${contactsCount !== 0 ? t('wizard.summary.contacts').toLowerCase() : t('wizard.summary.noContacts')}`}
                  </p>)}
                  
                </div>
              </div>
            ) : (
              <p className="mt-3 text-sm text-muted-foreground italic">
                {t('wizard.summary.notSelected')}
              </p>
            )}
          </div>

          {/* Voicemail Card */}
          <div className="rounded-lg border border-border p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <Mic className="h-4 w-4" />
                {t('wizard.summary.voicemail')}
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 gap-1.5 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                onClick={() => {
                  onEditStep(3);
                }}
              >
                <Pencil className="h-3.5 w-3.5" />
                {t('wizard.summary.select')}
              </Button>
            </div>
            {selectedVoicemail !== null ? (
              <div className="mt-3 flex items-center gap-3">
                <button
                  type="button"
                  onClick={handlePlayPause}
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors flex-shrink-0"
                >
                  {isPlaying ? (
                    <Pause className="h-5 w-5" />
                  ) : (
                    <Play className="h-5 w-5 ml-0.5" />
                  )}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">{selectedVoicemail.title}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex items-center gap-[2px] h-4 flex-1">
                      {generateWaveformBars().map((height, index) => {
                        const barProgress = (index / 16) * 100;
                        const isActive = isPlaying && barProgress <= playbackProgress;
                        return (
                          <div
                            key={index}
                            className={cn(
                              'w-1 rounded-full transition-all duration-150',
                              isActive ? 'bg-primary' : 'bg-muted-foreground/30',
                            )}
                            style={{ height: `${height}%` }}
                          />
                        );
                      })}
                    </div>
                    <span className="text-xs text-muted-foreground w-10 text-right flex-shrink-0">
                      {formatDuration(getVoicemailDuration())}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <p className="mt-3 text-sm text-muted-foreground italic">
                {t('wizard.summary.notSelected')}
              </p>
            )}
          </div>

          {/* Session Settings Card */}
          <div className="rounded-lg border border-border p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <Settings className="h-4 w-4" />
                {t('wizard.summary.settings')}
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 gap-1.5 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                onClick={() => {
                  onEditStep(4);
                }}
              >
                <Pencil className="h-3.5 w-3.5" />
                {t('wizard.summary.select')}
              </Button>
            </div>
            <div className="mt-3 space-y-2">
              {/* Shuffle Status */}
              <div className="flex items-center gap-2">
                <Shuffle className={cn('h-4 w-4', shuffleContacts ? 'text-primary' : 'text-muted-foreground')} />
                <span className="text-sm">
                  {shuffleContacts ? t('wizard.summary.shuffleOn') : t('wizard.summary.shuffleOff')}
                </span>
              </div>
              {/* Cut Off Status */}
              <div className="flex items-center gap-2">
                {ongoingCallAction === OngoingCallAction.CUT_OFF ? (
                  <PhoneOff className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <PhoneCall className="h-4 w-4 text-primary" />
                )}
                <span className="text-sm">
                  {ongoingCallAction === OngoingCallAction.CUT_OFF
                    ? t('wizard.summary.cutOffMode')
                    : t('wizard.summary.holdMode')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <ContactsPreviewModal
        isOpen={showContactsModal}
        onOpenChange={setShowContactsModal}
        contactListId={selectedContactList?.id ?? null}
        contactListTitle={selectedContactList?.title ?? ''}
        activeTab={currentContactsTab}
        salesforceNonce={salesforceNonce}
        outreachContacts={outreachContactsParams}
      />

      <IntentPreviewModal
        isOpen={showIntentModal}
        onOpenChange={setShowIntentModal}
        intent={selectedIntent}
      />
    </div>
  );
}
